import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import dayjs from "dayjs";
import {
  AddOutlined,
  CheckCircleOutline,
  DeleteOutline,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  StarBorderOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { GlobalConfirmDialog, globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { tooltipStyleProps } from "services/constants/Styles";
import { showToast } from "services/utils/Status";
import { ConfigTableCard } from "pages/PayRoll/Shared/ConfigurationUi";
import { MethodCards, ToneChip, monoSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import {
  checkBonusFormula,
  createBonusType,
  deleteBonusType,
  fetchBonusPayComponents,
  fetchBonusTypes,
  fetchBonusTypeVersions,
  fetchFormulaVariables,
  fetchWageComponents,
  updateBonusType,
} from "pages/PayRoll/Shared/bonusApi";
import {
  API_FIELD_TO_FORM,
  CALCULATION_BASIS_OPTIONS,
  DAYS_BASIS_OPTIONS,
  DIVISOR_OPTIONS,
  EMPTY_BONUS_TYPE,
  MAX_FORMULA_LENGTH,
  METHOD_OPTIONS,
  PERIOD_OPTIONS,
  STATUTORY_NOTICE,
  changedPayload,
  methodLabel,
  partPaymentsLabel,
  periodLabel,
  ruleSummary,
  toForm,
  toPayload,
  usesPartPayments,
  validateBonusType,
} from "./bonusTypesData";

const headSx = { ...designShell.tableHeadCellBase, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell };
const helperSx = { ...designType.captionText, mt: 1 };

const COLUMNS = [
  { key: "name", label: "Bonus Type" },
  { key: "method", label: "Method" },
  { key: "rule", label: "Rule" },
  { key: "period", label: "Period" },
  { key: "part", label: "Part Payments" },
  { key: "payslip", label: "Payslip Line" },
  { key: "status", label: "Status" },
  { key: "action", label: "Action" },
];

const tealSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.tealDark },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: designColors.teal },
};

const tealCheckboxSx = { p: 0, mr: 1, color: designColors.slate400, "&.Mui-checked": { color: designColors.teal } };

const SwitchRow = ({ checked, onChange, label }) => (
  <Mui.Stack direction="row" spacing={1} alignItems="center">
    <Mui.Switch checked={checked} onChange={(event) => onChange(event.target.checked)} inputProps={{ "aria-label": label }} sx={tealSwitchSx} />
    <Mui.Typography sx={designType.tableBodyCell}>{label}</Mui.Typography>
  </Mui.Stack>
);

// Wages used: multi-select of salary components (earned amount each month),
// with "Gross" first. Gross already includes the others, so picking Gross
// clears the rest and picking a component clears Gross.
const WageComponentsField = ({ value, onChange, options, savedNames, savedIds, error }) => {
  const known = new Map(options.map((option) => [option.value, option]));
  const selected = value.map((id) => known.get(id) || {
    value: id,
    label: savedNames?.[savedIds?.indexOf(id)] || (id === "GROSS" ? "Gross" : `Component ${id}`),
    includesOvertime: false,
  });
  const anyOvertime = selected.some((option) => option.includesOvertime);
  return (
    <Mui.Box>
      <Mui.Autocomplete
        multiple
        size="small"
        disableCloseOnSelect
        options={options}
        value={selected}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) => option.value === current.value}
        onChange={(event, picked) => {
          let next = picked.map((option) => option.value);
          const added = next.find((id) => !value.includes(id));
          if (added === "GROSS") next = ["GROSS"];
          else if (added !== undefined) next = next.filter((id) => id !== "GROSS");
          onChange(next);
        }}
        renderOption={(props, option, { selected: isSelected }) => (
          <li {...props} key={option.value}>
            <Mui.Checkbox size="small" checked={isSelected} sx={tealCheckboxSx} />
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <Mui.TextField {...params} placeholder={value.length ? "" : "Select wages used"} error={Boolean(error)} sx={globalTextFieldSx} />
        )}
      />
      {selected.length ? (
        <Mui.Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75, color: anyOvertime ? "#B54708" : "#067647" }}>
          {anyOvertime ? <WarningAmberOutlined sx={{ fontSize: 16 }} /> : <CheckCircleOutline sx={{ fontSize: 16 }} />}
          <Mui.Typography sx={{ fontSize: 12.5, fontWeight: designWeights.semibold }}>
            {anyOvertime ? "Includes overtime" : "Overtime not included"}
          </Mui.Typography>
        </Mui.Stack>
      ) : null}
    </Mui.Box>
  );
};

// Formula box, clickable names that insert at the cursor, and Check formula
// (POST types/check-formula/). `check` is the last result, for this text.
const FormulaField = ({ value, onChange, names, error, check, checking, onCheck }) => {
  const inputRef = useRef(null);
  const insert = (text) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange((value.slice(0, start) + text + value.slice(end)).slice(0, MAX_FORMULA_LENGTH));
    const caret = start + text.length;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(caret, caret);
    });
  };
  return (
    <Mui.Box>
      <Mui.Box sx={{ border: `1px solid ${error ? "#DC2626" : designColors.borderInput}`, borderRadius: designRadii.control, backgroundColor: designColors.white }}>
        <Mui.InputBase
          inputRef={inputRef}
          value={value}
          onChange={(event) => onChange(event.target.value.slice(0, MAX_FORMULA_LENGTH))}
          multiline
          minRows={2}
          placeholder="e.g. IF([Days Present] >= 26, 500, 0)"
          inputProps={{ "aria-label": "Formula", spellCheck: false }}
          sx={{ width: "100%", px: 1.5, py: 1, fontSize: 14, ...monoSx }}
        />
      </Mui.Box>
      <Mui.Typography sx={{ ...designType.captionText, mt: 0.75 }}>
        Use names in [ ] — e.g. [Basic], [DA], [Gross], [Days Present]. Functions: IF, MIN, MAX, ROUND. {value.length}/{MAX_FORMULA_LENGTH}
      </Mui.Typography>
      {names.length ? (
        <Mui.Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75} sx={{ mt: 1 }}>
          {names.map((name) => (
            <Mui.Button
              key={name}
              size="small"
              onClick={() => insert(`[${name}]`)}
              sx={{
                ...monoSx,
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: 12,
                textTransform: "none",
                borderRadius: designRadii.compactButton,
                border: "1px solid #A7E3CB",
                backgroundColor: "#E7F8EF",
                color: "#067647",
              }}
            >
              [{name}]
            </Mui.Button>
          ))}
        </Mui.Stack>
      ) : null}
      <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
        <SecondaryButton onClick={onCheck} disabled={checking || !value.trim()}>
          {checking ? "Checking..." : "Check formula"}
        </SecondaryButton>
        {check ? (
          <Mui.Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ color: check.valid ? "#067647" : "#DC2626", minWidth: 0 }}>
            {check.valid ? <CheckCircleOutline sx={{ fontSize: 16, mt: "2px" }} /> : <ErrorOutline sx={{ fontSize: 16, mt: "2px" }} />}
            <Mui.Typography sx={{ fontSize: 13, fontWeight: designWeights.semibold }}>
              {check.valid ? "Formula is valid" : check.errors.join(" ")}
            </Mui.Typography>
          </Mui.Stack>
        ) : null}
      </Mui.Stack>
    </Mui.Box>
  );
};

// Every saved version, newest first (GET types/{id}/versions/). Loaded the
// first time it is opened.
const VersionHistory = ({ domain, typeId, currentVersion }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, error: "", versions: null });
  useEffect(() => {
    if (!open || state.versions || state.loading) return undefined;
    const controller = new AbortController();
    setState({ loading: true, error: "", versions: null });
    fetchBonusTypeVersions({ domain, id: typeId, signal: controller.signal })
      .then(({ versions }) => { if (!controller.signal.aborted) setState({ loading: false, error: "", versions }); })
      .catch((error) => { if (!controller.signal.aborted) setState({ loading: false, error: error.message, versions: null }); });
    return () => controller.abort();
  }, [open, domain, typeId, state.versions, state.loading]);

  return (
    <FormSection title="Version history">
      <Mui.Box sx={{ gridColumn: "1 / -1", mt: -1 }}>
        <Mui.Typography sx={designType.captionText}>
          Current version v{currentVersion}. A used type gets a new version when its rules change; earlier runs and entries keep theirs.
        </Mui.Typography>
        <Mui.Button
          size="small"
          onClick={() => setOpen((value) => !value)}
          endIcon={open ? <ExpandLess /> : <ExpandMore />}
          sx={{ mt: 1, px: 0, textTransform: "none", color: designColors.tealDark, fontWeight: designWeights.semibold }}
        >
          {open ? "Hide versions" : "Show versions"}
        </Mui.Button>
        {open ? (
          state.loading ? (
            <Mui.Box sx={{ py: 2, textAlign: "center" }}>
              <Mui.CircularProgress size={20} sx={{ color: designColors.teal }} aria-label="Loading versions" />
            </Mui.Box>
          ) : state.error ? (
            <Mui.Alert severity="error" sx={{ mt: 1 }} action={<Mui.Button color="inherit" onClick={() => setState({ loading: false, error: "", versions: null })}>Retry</Mui.Button>}>
              {state.error}
            </Mui.Alert>
          ) : (
            <Mui.Stack spacing={1} sx={{ mt: 1 }}>
              {(state.versions || []).map((version) => (
                <Mui.Box key={version.version} sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, px: 1.5, py: 1 }}>
                  <Mui.Stack direction="row" spacing={1} alignItems="center">
                    <ToneChip label={`v${version.version}`} tone={version.version === currentVersion ? "teal" : "slate"} />
                    {version.version === currentVersion ? <Mui.Typography sx={designType.captionText}>Current</Mui.Typography> : null}
                    <Mui.Box sx={{ flex: 1 }} />
                    <Mui.Typography sx={designType.captionText}>
                      {version.createdat ? dayjs(version.createdat).format("DD MMM YYYY, HH:mm") : ""}
                      {version.createdby ? ` · ${version.createdby}` : ""}
                    </Mui.Typography>
                  </Mui.Stack>
                  <Mui.Typography sx={{ ...designType.tableBodyCell, mt: 0.5 }}>
                    {version.name} · {methodLabel(version)} · {ruleSummary(version)}
                  </Mui.Typography>
                </Mui.Box>
              ))}
            </Mui.Stack>
          )
        ) : null}
      </Mui.Box>
    </FormSection>
  );
};

const exampleForPercent = (form) => {
  const rate = Number(form.rate);
  if (!(rate > 0)) return null;
  const earned = 12000;
  const ceiling = Number(form.ceiling) > 0 ? Number(form.ceiling) : null;
  const used = ceiling ? Math.min(earned, ceiling) : earned;
  const total = (used * 12 * rate) / 100;
  return `Example: wages ₹${earned.toLocaleString("en-IN")} a month${ceiling ? ` — capped at ₹${ceiling.toLocaleString("en-IN")}` : ""} × 12 months × ${rate}% = ₹${total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}. Sample values — enter your own.`;
};

const exampleForPerDay = (form) => {
  const amount = Number(form.perDayAmount);
  if (!(amount > 0)) return null;
  const fmt = (days) => `₹${(days * amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  return `Example: 23 days present × ₹${amount.toLocaleString("en-IN")} = ${fmt(23)}. With Paid Days (25) it would be ${fmt(25)}.`;
};

// Add / Edit Bonus Type side panel (spec Figures A2, A3, A3b). Only the
// fields for the chosen method are shown.
const BonusTypePanel = ({
  domain, companyId, editing, rows, wageOptions, formulaNames, saving, serverErrors, onClose, onSave, onDelete,
}) => {
  const [form, setForm] = useState(() => (editing ? toForm(editing) : { ...EMPTY_BONUS_TYPE }));
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(() => new Set());
  useEffect(() => setTouched(new Set()), [serverErrors]);
  const errors = validateBonusType(form, rows, editing?.id);
  const shownErrors = {
    ...Object.fromEntries(Object.entries(serverErrors || {}).filter(([key]) => !touched.has(key))),
    ...(submitted ? errors : {}),
  };
  const set = (key) => (value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => new Set(prev).add(key));
  };

  // Payslip line: free Other Earnings components (plus this type's own).
  const [payslip, setPayslip] = useState({ loading: true, options: [], error: "" });
  useEffect(() => {
    const controller = new AbortController();
    fetchBonusPayComponents({ domain, companyId, bonusTypeId: editing?.id, signal: controller.signal })
      .then((options) => { if (!controller.signal.aborted) setPayslip({ loading: false, options, error: "" }); })
      .catch((error) => { if (!controller.signal.aborted) setPayslip({ loading: false, options: [], error: `Couldn't load payslip lines: ${error.message}` }); });
    return () => controller.abort();
  }, [domain, companyId, editing?.id]);
  const payslipOptions = editing?.pay_component_id && !payslip.options.some((option) => option.value === editing.pay_component_id)
    ? [...payslip.options, { value: editing.pay_component_id, label: editing.pay_component_name || `Pay component ${editing.pay_component_id}` }]
    : payslip.options;

  // Check formula result, kept only for the text it was run on.
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const runCheck = async () => {
    const formula = form.formula;
    setChecking(true);
    try {
      const result = await checkBonusFormula({ domain, companyId, formula });
      setCheck({ formula, ...result });
    } catch (error) {
      setCheck({ formula, valid: false, errors: [error.message] });
    } finally {
      setChecking(false);
    }
  };
  const shownCheck = check && check.formula === form.formula ? check : null;

  const handleSave = () => {
    setSubmitted(true);
    if (Object.keys(errors).length || saving) return;
    onSave(form);
  };

  const m = form.method;
  const minDaysSet = String(form.minDays).trim() !== "";
  const daysCountedField = (required) => (
    <Field label={`Days counted${required ? " *" : ""}`} helper="Days Present or Paid Days" error={shownErrors.daysBasis}>
      <SelectField value={form.daysBasis} onChange={set("daysBasis")} options={DAYS_BASIS_OPTIONS} placeholder="Select days counted" error={shownErrors.daysBasis} />
    </Field>
  );
  const minDaysField = (
    <Field label="Minimum days in period" helper="Optional. Fewer days → left out" error={shownErrors.minDays}>
      <TextField type="number" value={form.minDays} onChange={set("minDays")} placeholder="e.g. 30" error={shownErrors.minDays} />
    </Field>
  );
  const periodField = (wide) => (
    <Mui.Box sx={wide ? { gridColumn: "1 / -1" } : undefined}>
      <Field label="Default period *" helper="Default months in Calculate Bonus" error={shownErrors.defaultPeriod}>
        <SelectField value={form.defaultPeriod} onChange={set("defaultPeriod")} options={PERIOD_OPTIONS} placeholder="Select period" error={shownErrors.defaultPeriod} />
      </Field>
    </Mui.Box>
  );
  const wagesField = (
    <Field label="Wages used *" helper="Earned amount each month" error={shownErrors.wageComponents} wide>
      <WageComponentsField
        value={form.wageComponents}
        onChange={set("wageComponents")}
        options={wageOptions}
        savedIds={editing?.wage_components}
        savedNames={editing?.wage_component_names}
        error={shownErrors.wageComponents}
      />
    </Field>
  );
  const daysHelp = (
    <Mui.Typography sx={helperSx}>
      Days Present = days actually attended (paid leave not counted). Paid Days = days paid in payroll (includes paid leave and paid holidays). Overtime hours never add days.
    </Mui.Typography>
  );

  const nextVersion = (editing?.version || 1) + 1;

  return (
    <GlobalSidePanel
      open
      onClose={onClose}
      title={editing ? "Edit Bonus Type" : "Add Bonus Type"}
      subtitle={editing ? editing.name : "How the bonus is worked out, the period and the payslip line."}
      bodySx={panelBodySx}
      headerActions={editing ? (
        <Mui.Tooltip
          title={editing.can_delete ? "Delete bonus type" : "Used in a bonus run or entry, so it can't be deleted. Make it Inactive instead."}
          arrow
          slotProps={tooltipStyleProps}
        >
          <span>
            <Mui.IconButton
              size="small"
              aria-label="Delete bonus type"
              disabled={saving || !editing.can_delete}
              onClick={onDelete}
              sx={{ ...designShell.panelSoftIconButton, color: "#DC2626" }}
            >
              <DeleteOutline fontSize="small" />
            </Mui.IconButton>
          </span>
        </Mui.Tooltip>
      ) : null}
      footer={
        <PanelActionFooter
          onCancel={() => { if (!saving) onClose(); }}
          primaryLabel={saving ? "Saving..." : editing ? "Save" : "Add type"}
          primaryProps={{ onClick: handleSave, disabled: saving }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        {editing?.is_used ? (
          <RequestInfoBanner
            title="This type has been used"
            body={`Rule changes are saved as version ${nextVersion}; earlier runs and entries keep version ${editing.version}. Changing only Active does not make a new version.`}
          />
        ) : null}

        <FormSection title="Type">
          <Field label="Name *" error={shownErrors.name} wide>
            <TextField value={form.name} onChange={(value) => set("name")(value.slice(0, 100))} placeholder="e.g. Statutory Bonus" error={shownErrors.name} />
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Mui.FormControlLabel
              control={<Mui.Checkbox checked={form.isStatutory} onChange={(event) => set("isStatutory")(event.target.checked)} sx={tealCheckboxSx} />}
              label="This is a statutory bonus"
              sx={{ m: 0, "& .MuiFormControlLabel-label": designType.tableBodyCell }}
            />
            {form.isStatutory ? (
              <Mui.Box sx={{ mt: 1.5 }}>
                <RequestInfoBanner tone="warning" title="Statutory bonus" body={STATUTORY_NOTICE} />
              </Mui.Box>
            ) : null}
          </Mui.Box>
        </FormSection>

        <FormSection title="How is the bonus worked out? *">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <MethodCards value={m} options={METHOD_OPTIONS} onChange={set("method")} columns={2} />
            {shownErrors.method ? (
              <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.75 }}>{shownErrors.method}</Mui.Typography>
            ) : null}
          </Mui.Box>
        </FormSection>

        {m === "percent_of_wages" ? (
          <FormSection
            title="% of wages"
            footer={exampleForPercent(form) ? <Mui.Typography sx={helperSx}>{exampleForPercent(form)}</Mui.Typography> : null}
          >
            <Field label="Bonus rate (%) *" helper="0.01 to 100" error={shownErrors.rate}>
              <TextField type="number" value={form.rate} onChange={set("rate")} placeholder="e.g. 8.33" error={shownErrors.rate} />
            </Field>
            <Field label="Wage ceiling per month (₹)" helper="Optional. Wages used capped at this" error={shownErrors.ceiling}>
              <TextField type="number" value={form.ceiling} onChange={set("ceiling")} placeholder="No ceiling" error={shownErrors.ceiling} />
            </Field>
            {wagesField}
            <Field label="Pay only if monthly wage ≤ (₹)" helper="Optional. Gross earned above → left out" error={shownErrors.wageLimit}>
              <TextField type="number" value={form.wageLimit} onChange={set("wageLimit")} placeholder="No limit" error={shownErrors.wageLimit} />
            </Field>
            {minDaysField}
            {daysCountedField(minDaysSet)}
            {periodField(false)}
          </FormSection>
        ) : null}

        {m === "fixed_or_days" ? (
          <FormSection
            title="Fixed amount / days' wages"
            footer={form.calculationBasis === "days_wages" ? (
              <Mui.Typography sx={helperSx}>
                Days × (wages earned in the To month ÷ divisor). Only the To month is used.
              </Mui.Typography>
            ) : null}
          >
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <MethodCards value={form.calculationBasis} options={CALCULATION_BASIS_OPTIONS} onChange={set("calculationBasis")} columns={2} />
              {shownErrors.calculationBasis ? (
                <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.75 }}>{shownErrors.calculationBasis}</Mui.Typography>
              ) : null}
            </Mui.Box>
            {form.calculationBasis === "fixed_amount" ? (
              <Field label="Fixed amount (₹) *" helper="Paid once per employee per run" error={shownErrors.fixedAmount} wide>
                <TextField type="number" value={form.fixedAmount} onChange={set("fixedAmount")} placeholder="e.g. 1000" error={shownErrors.fixedAmount} />
              </Field>
            ) : null}
            {form.calculationBasis === "days_wages" ? (
              <>
                <Field label="Days *" helper="0.5 to 365" error={shownErrors.days}>
                  <TextField type="number" value={form.days} onChange={set("days")} placeholder="e.g. 15" error={shownErrors.days} />
                </Field>
                <Field label="Divisor *" helper="Days in the To month's wage" error={shownErrors.dayDivisor}>
                  <SelectField value={form.dayDivisor} onChange={set("dayDivisor")} options={DIVISOR_OPTIONS} placeholder="Select divisor" error={shownErrors.dayDivisor} />
                </Field>
                {wagesField}
              </>
            ) : null}
          </FormSection>
        ) : null}

        {m === "per_day" ? (
          <FormSection
            title="Fixed amount per day"
            footer={
              <>
                {daysHelp}
                {exampleForPerDay(form) ? <Mui.Typography sx={helperSx}>{exampleForPerDay(form)}</Mui.Typography> : null}
              </>
            }
          >
            <Field label="Amount per day (₹) *" error={shownErrors.perDayAmount}>
              <TextField type="number" value={form.perDayAmount} onChange={set("perDayAmount")} placeholder="e.g. 20" error={shownErrors.perDayAmount} />
            </Field>
            {daysCountedField(true)}
            {minDaysField}
            {periodField(false)}
          </FormSection>
        ) : null}

        {m === "enter_amount" ? (
          <FormSection title="Enter amount" info="HR types in or imports the amount for each person. There are no rules to set." />
        ) : null}

        {m === "formula" ? (
          <>
            <FormSection title="Formula *">
              <Mui.Box sx={{ gridColumn: "1 / -1" }}>
                <FormulaField
                  value={form.formula}
                  onChange={set("formula")}
                  names={formulaNames}
                  error={shownErrors.formula}
                  check={shownCheck}
                  checking={checking}
                  onCheck={runCheck}
                />
                {shownErrors.formula ? (
                  <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.75 }}>{shownErrors.formula}</Mui.Typography>
                ) : null}
              </Mui.Box>
            </FormSection>
            <FormSection title="Period and eligibility" footer={minDaysSet ? daysHelp : null}>
              {periodField(true)}
              {minDaysField}
              {daysCountedField(minDaysSet)}
            </FormSection>
          </>
        ) : null}

        {m ? (
          <FormSection title="Payment and payslip">
            {usesPartPayments(m) ? (
              <Mui.Box sx={{ gridColumn: "1 / -1" }}>
                <SwitchRow checked={form.allowPart} onChange={set("allowPart")} label="Allow part payments (e.g. advance at Diwali, balance after March)" />
                <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>
                  Each run shows the full bonus due, what is already paid for the same period, and pays part or the balance.
                </Mui.Typography>
              </Mui.Box>
            ) : null}
            <Field
              label="Payslip line (pay component) *"
              helper="Each bonus type needs its own Other Earnings line"
              error={shownErrors.payComponentId || payslip.error}
              wide
            >
              <SelectField
                value={form.payComponentId}
                onChange={(value) => set("payComponentId")(value === "" ? "" : Number(value))}
                options={payslipOptions}
                placeholder={payslip.loading ? "Loading..." : payslipOptions.length ? "Select payslip line" : "No free Other Earnings component — add one in Salary Components"}
                error={shownErrors.payComponentId || payslip.error}
              />
            </Field>
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <SwitchRow checked={form.active} onChange={set("active")} label={form.active ? "Active — can be used in Calculate Bonus and new entries" : "Inactive — not offered for new runs or entries"} />
            </Mui.Box>
          </FormSection>
        ) : null}

        {editing ? <VersionHistory domain={domain} typeId={editing.id} currentVersion={editing.version} /> : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const renderCell = (row, column, onEdit) => {
  switch (column.key) {
    case "name":
      return (
        <Mui.Stack direction="row" spacing={1} alignItems="center">
          <span>{row.name}</span>
          {row.is_statutory ? <ToneChip label="Statutory" tone="amber" /> : null}
        </Mui.Stack>
      );
    case "method":
      return methodLabel(row);
    case "rule":
      return ruleSummary(row);
    case "period":
      return periodLabel(row);
    case "part":
      return partPaymentsLabel(row);
    case "payslip":
      return row.pay_component_name || "—";
    case "status":
      return <ToneChip label={row.status === "Inactive" ? "Inactive" : "Active"} tone={row.status === "Inactive" ? "slate" : "green"} />;
    case "action":
      return (
        <Mui.Button
          size="small"
          onClick={(event) => { event.stopPropagation(); onEdit(); }}
          sx={{ minWidth: 0, px: 0.5, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
        >
          Edit
        </Mui.Button>
      );
    default:
      return null;
  }
};

// Bonus Types card on Payroll Settings > Bonus (spec Figure A1), on the
// bonus types/ API. The server adds the 4 starter types the first time a
// company opens it (sample values, so the ones with numbers start Inactive).
// Saving here is separate from the page's Save Changes (settings only).
export const BonusTypesCard = ({ domain, companyId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [wageOptions, setWageOptions] = useState([]);
  const [formulaNames, setFormulaNames] = useState([]);
  // { mode: "add" | "edit", id }
  const [panel, setPanel] = useState(null);
  const panelRow = panel?.id != null ? rows.find((row) => row.id === panel.id) || null : null;
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    fetchBonusTypes({ domain, companyId, signal: controller.signal })
      .then((types) => { if (!controller.signal.aborted) setRows(types); })
      .catch((error) => { if (!controller.signal.aborted) setLoadError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, companyId, reload]);

  // Drop-down lists for the panel. A failure only leaves the list empty;
  // the server still checks the saved values.
  useEffect(() => {
    const controller = new AbortController();
    fetchWageComponents({ domain, companyId, signal: controller.signal })
      .then((options) => { if (!controller.signal.aborted) setWageOptions(options); })
      .catch(() => {});
    fetchFormulaVariables({ domain, companyId, signal: controller.signal })
      .then(({ system, components }) => {
        if (controller.signal.aborted) return;
        const names = [...components.map((item) => item.name), ...system.map((item) => item.name)];
        setFormulaNames([...new Set(names.filter(Boolean))]);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [domain, companyId]);

  const closePanel = useCallback(() => {
    if (saving) return;
    setPanel(null);
    setServerErrors({});
  }, [saving]);

  const openEdit = (row) => { setServerErrors({}); setPanel({ mode: "edit", id: row.id }); };

  const handleSave = async (form) => {
    const editingRow = panel?.mode === "edit" ? panelRow : null;
    let payload = toPayload(form);
    if (editingRow) {
      payload = changedPayload(form, editingRow);
      if (!Object.keys(payload).length) {
        setPanel(null);
        showToast("info", "No changes to save.");
        return;
      }
    }
    setSaving(true);
    setServerErrors({});
    try {
      const result = editingRow
        ? await updateBonusType({ domain, id: editingRow.id, payload })
        : await createBonusType({ domain, companyId, payload });
      if (result.type) {
        setRows((prev) => (editingRow
          ? prev.map((row) => (row.id === editingRow.id ? result.type : row))
          : [...prev, result.type]));
      } else {
        setReload((value) => value + 1);
      }
      showToast("success", result.message || (editingRow ? "Bonus type saved." : "Bonus type added."));
      setPanel(null);
    } catch (error) {
      setServerErrors(Object.fromEntries(
        Object.entries(error.fieldErrors || {}).map(([field, message]) => [API_FIELD_TO_FORM[field] || field, message]),
      ));
      showToast("error", error.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const target = deleting;
    setSaving(true);
    try {
      await deleteBonusType({ domain, id: target.id });
      setRows((prev) => prev.filter((row) => row.id !== target.id));
      setPanel(null);
      showToast("success", "Bonus type deleted.");
    } catch (error) {
      showToast("error", error.message);
      // TYPE_IN_USE: used since the list loaded; reload so Delete greys out.
      if (error.code === "TYPE_IN_USE") setReload((value) => value + 1);
    } finally {
      setDeleting(null);
      setSaving(false);
    }
  };

  return (
    <ConfigTableCard
      icon={StarBorderOutlined}
      tint="pink"
      title="Bonus Types"
      description="How each bonus is worked out, the period it covers, whether it can be paid in parts, and the payslip line."
      action={
        <PrimaryButton startIcon={<AddOutlined />} disabled={loading || Boolean(loadError)} onClick={() => { setServerErrors({}); setPanel({ mode: "add", id: null }); }}>
          Add type
        </PrimaryButton>
      }
      note={
        <>
          Methods — <b>% of wages</b> · <b>Fixed amount / days&apos; wages</b> · <b>Fixed amount per day</b> · <b>Enter amount</b> · <b>Custom formula</b>.
          {" "}Rates and limits shown are samples: each company enters its own and is responsible for them. A used type can be made Inactive but not deleted.
        </>
      }
    >
      <Mui.TableContainer sx={{ overflowX: "auto" }}>
        <Mui.Table size="small" sx={{ minWidth: 960 }}>
          <Mui.TableHead>
            <Mui.TableRow>
              {COLUMNS.map((column) => (
                <Mui.TableCell key={column.key} sx={headSx}>{column.label}</Mui.TableCell>
              ))}
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {loading ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={COLUMNS.length} sx={{ ...cellSx, textAlign: "center", py: 4 }}>
                  <Mui.CircularProgress size={22} sx={{ color: designColors.teal }} aria-label="Loading bonus types" />
                </Mui.TableCell>
              </Mui.TableRow>
            ) : loadError ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={COLUMNS.length} sx={{ ...cellSx, py: 2 }}>
                  <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setReload((value) => value + 1)}>Retry</Mui.Button>}>
                    {loadError}
                  </Mui.Alert>
                </Mui.TableCell>
              </Mui.TableRow>
            ) : rows.length === 0 ? (
              <Mui.TableRow>
                <Mui.TableCell colSpan={COLUMNS.length} sx={{ ...cellSx, textAlign: "center", py: 4, color: designColors.slate600 }}>
                  No bonus types yet. Add one to start paying bonus.
                </Mui.TableCell>
              </Mui.TableRow>
            ) : (
              rows.map((row) => (
                <Mui.TableRow
                  key={row.id}
                  hover
                  tabIndex={0}
                  aria-label={`Edit ${row.name}`}
                  onClick={() => openEdit(row)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openEdit(row);
                    }
                  }}
                  sx={{ cursor: "pointer", ...(panel?.id === row.id ? designShell.neutralSelectedRow : {}) }}
                >
                  {COLUMNS.map((column) => (
                    <Mui.TableCell key={column.key} sx={cellSx}>{renderCell(row, column, () => openEdit(row))}</Mui.TableCell>
                  ))}
                </Mui.TableRow>
              ))
            )}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.TableContainer>

      {panel?.mode === "add" || (panel?.mode === "edit" && panelRow) ? (
        <BonusTypePanel
          key={panel.mode === "edit" ? `${panelRow.id}:${panelRow.version}` : "new"}
          domain={domain}
          companyId={companyId}
          editing={panel.mode === "edit" ? panelRow : null}
          rows={rows}
          wageOptions={wageOptions}
          formulaNames={formulaNames}
          saving={saving}
          serverErrors={serverErrors}
          onClose={closePanel}
          onSave={handleSave}
          onDelete={() => setDeleting(panelRow)}
        />
      ) : null}

      <GlobalConfirmDialog
        open={Boolean(deleting)}
        title="Delete bonus type?"
        confirmLabel="Delete"
        cancelLabel="Keep"
        onCancel={() => { if (!saving) setDeleting(null); }}
        onConfirm={confirmDelete}
      >
        {deleting
          ? `"${deleting.name}" will be removed from the list. This cannot be undone${deleting.is_starter ? " — starter types are not created again" : ""}.`
          : null}
      </GlobalConfirmDialog>
    </ConfigTableCard>
  );
};
